import React, { useCallback, useEffect, useState } from "react";

import {
  emitEventImpression,
  emitProductImpression,
  emitRecommenderImpression,
  EVENT_RECOMMENDER_EVENT_TYPE,
  FromProduct,
} from "@catchoftheday/analytics";
import {
  Box,
  BoxProps,
  Carousel,
  CarouselProps,
  Heading,
} from "@catchoftheday/cg-components";
import { ProductCardPlaceholder } from "@catchoftheday/common-vendors";
import { useBreakpoint } from "@catchoftheday/theme";
import { centsToDollars, useIsInViewport } from "@catchoftheday/utilities";

import {
  RECOMMENDER_DESKTOP_LIMIT,
  RECOMMENDER_MOBILE_LIMIT,
} from "@personalisation/consts";

import {
  getRecommenderTilesPerBreakpoint,
  RecommenderTrackingData,
} from "../utils";

type Sizes = "xs" | "sm" | "md" | "lg" | "xl";

export type RecommenderCarouselProps = {
  heading?: string;
  wrapperProps?: BoxProps;
  tiles: React.ReactNode[];
  tilesPerBreakpoint?: Record<Sizes, number>;
  trackingData: RecommenderTrackingData[];
  sourceType: number;
  sourceId?: string;
  fromProduct: FromProduct;
  fullWidth?: boolean;
  carouselProps?: Omit<CarouselProps, "slides">;
  /** Sets max number of tiles for pagination */
  maxTiles?: number;
  /** Enables pagination */
  paginate?: boolean;
};

export const BaseRecommenderCarousel = ({
  tiles,
  tilesPerBreakpoint,
  trackingData,
  sourceType,
  sourceId,
  fromProduct,
  carouselProps,
  maxTiles,
  paginate,
}: RecommenderCarouselProps) => {
  const { isMobile } = useBreakpoint({ minusOnePx: true });
  const totalTiles =
    maxTiles ||
    (isMobile ? RECOMMENDER_MOBILE_LIMIT : RECOMMENDER_DESKTOP_LIMIT);
  const [carouselState, setCarouselState] = useState({
    slidesPerView: 0,
    currentIndex: 0,
  });
  const [lastTrackedItemPosition, setLastTrackedItemPosition] = useState(0);
  const { slidesPerBreakpoint, buttonOffset, showBullets } =
    carouselProps || {};
  const recommendationsPerBreakpoint =
    slidesPerBreakpoint || getRecommenderTilesPerBreakpoint(tilesPerBreakpoint);
  const tilesWithPlaceholders =
    totalTiles > tiles.length
      ? tiles.concat(
          [...Array(totalTiles - tiles.length).keys()].map((_, index) => (
            <Box
              key={`product-placeholder-${index}`}
              px={{ xs: "2px", md: "10px" }}
              width="100%"
              height="100%"
            >
              <ProductCardPlaceholder
                aria-label="Product card loading"
                uniqueKey={`tile-${index}`}
                height="100%"
              />
            </Box>
          ))
        )
      : tiles;

  const trackItemImpressions = useCallback(() => {
    const { slidesPerView, currentIndex } = carouselState;
    const itemsLength = trackingData.length;
    const visibleNumberOfSlides =
      slidesPerView > itemsLength ? itemsLength : slidesPerView;

    if (visibleNumberOfSlides > 0 && itemsLength > currentIndex) {
      Array.from({ length: visibleNumberOfSlides }).forEach((_v, i) => {
        const itemIndex = currentIndex + i;
        const item = trackingData[itemIndex];
        if (item && itemIndex >= lastTrackedItemPosition) {
          item.type == "event"
            ? emitEventImpression({
                event_id: Number(item.id),
                source_type: item.sourceType || sourceType,
                position: itemIndex + 1,
                badge_text: item.badgeText || null,
                event_type: EVENT_RECOMMENDER_EVENT_TYPE,
                schedule_entry_id: null,
                subtitle: null,
                title: item.title,
                ...fromProduct,
              })
            : emitProductImpression({
                offer_id: Number(item.offerId),
                source_id: sourceId || "Product page Recommendation",
                source_position: itemIndex + 1,
                source_type: item.sourceType || sourceType,
                product_id: Number(item.id),
                product_title: item.title,
                displayed_price: item.sellPrice
                  ? centsToDollars(item.sellPrice)
                  : null,
                ...fromProduct,
              });
          const trackedUpTo = currentIndex + visibleNumberOfSlides;
          if (trackedUpTo > lastTrackedItemPosition) {
            setLastTrackedItemPosition(trackedUpTo);
          }
        }
      });
    }
  }, [
    trackingData,
    fromProduct,
    sourceType,
    carouselState,
    lastTrackedItemPosition,
  ]);

  const { ref } = useIsInViewport({
    shouldTrackVisibility: !!trackingData,
    // Trigger impression events if 75% of the component is in the viewport on mobile
    threshold: isMobile ? 0.75 : 1,
    onComponentSeen: () => {
      trackItemImpressions();
      emitRecommenderImpression({
        source_type: sourceType,
        recommended_product_count: totalTiles,
        ...fromProduct,
      });
    },
  });

  useEffect(() => {
    // Only track impressions here after the first slide has been looked at
    // The first slide is tracked within onComponentSeen
    if (carouselState.currentIndex > 0) {
      trackItemImpressions();
    }
  }, [trackItemImpressions, carouselState.currentIndex]);

  return (
    <Carousel
      {...carouselProps}
      containerProps={{
        ...(carouselProps?.containerProps || {}),
        ref: ref,
      }}
      slides={paginate ? tilesWithPlaceholders : tiles}
      slidesPerBreakpoint={recommendationsPerBreakpoint}
      buttonOffset={buttonOffset || [-40, -20]}
      showBullets={!!showBullets}
      onSlideChange={({ slidesPerView, currentIndex, direction }) => {
        setCarouselState({
          slidesPerView,
          currentIndex,
        });
        carouselProps?.onSlideChange?.({
          slidesPerView,
          currentIndex,
          direction,
        });
      }}
      onCarouselInit={(slidesPerView) => {
        setCarouselState({
          slidesPerView,
          currentIndex: 0,
        });
      }}
    />
  );
};

export const RecommenderCarousel = ({
  heading,
  wrapperProps,
  ...otherProps
}: RecommenderCarouselProps) => (
  <Box>
    <Box
      maxWidth="1340px"
      margin="auto"
      position="relative"
      overflow="hidden"
      px={{ xs: "0px", md: "80px" }}
      mx={{ xs: "-30px", md: "auto" }}
      pb="40px"
      {...wrapperProps}
    >
      {heading && <Heading.h4 textAlign="center">{heading}</Heading.h4>}
      <BaseRecommenderCarousel {...otherProps} />
    </Box>
  </Box>
);
